import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldAlert, Wrench, CheckCircle2, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError, ksh } from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { timeAgo } from "@/components/admin/_shared";

/**
 * Data Health tab — scans the orders collection for drift rows
 * (order.status is in the paid lifecycle but order.payment_status ≠ "paid")
 * and offers one-click cleanup.
 *
 * Backend endpoints:
 *  - GET  /api/admin/data-health/drift          → { drift_count, orders }
 *  - POST /api/admin/data-health/drift/repair   → { repaired, order_ids } (super_admin only)
 *  - POST /api/admin/data-health/orders/{id}/repair → { repaired } (super_admin only)
 */
export default function DataHealthTab({ user }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [driftCount, setDriftCount] = useState(0);
  const [repairing, setRepairing] = useState(false);

  const isSuper = user?.role === "super_admin" || user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-health/drift");
      setRows(data.orders || []);
      setDriftCount(Number(data.drift_count || 0));
    } catch (err) {
      devLog.debug("data-health scan failed", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not scan for drift orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const repairAll = async () => {
    if (!isSuper) return;
    if (!driftCount) return;
    if (!window.confirm(`Repair all ${driftCount} drift order${driftCount === 1 ? "" : "s"}? This snaps payment_status back to "paid" and logs an audit-trail entry per row.`)) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/data-health/drift/repair");
      toast.success(`Repaired ${data.repaired} order${data.repaired === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      devLog.debug("data-health repair failed", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Repair failed — try again.");
    } finally {
      setRepairing(false);
    }
  };

  const repairOne = async (orderId) => {
    if (!isSuper) return;
    setRepairing(true);
    try {
      const { data } = await api.post(`/admin/data-health/orders/${orderId}/repair`);
      if (data.repaired) toast.success("Order healed.");
      else toast.message(data.message || "Nothing to heal.");
      await load();
    } catch (err) {
      devLog.debug("single repair failed", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Repair failed.");
    } finally {
      setRepairing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#525252] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Scanning orders for drift…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-health-tab">
      {/* Header card */}
      <div className={`border p-5 ${driftCount === 0 ? "border-[#059669] bg-[#059669]/5" : "border-[#F59E0B] bg-[#F59E0B]/5"}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            {driftCount === 0 ? (
              <CheckCircle2 className="w-6 h-6 text-[#059669] shrink-0 mt-0.5" strokeWidth={2.5} />
            ) : (
              <ShieldAlert className="w-6 h-6 text-[#F59E0B] shrink-0 mt-0.5" strokeWidth={2.5} />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Data Health · Payment Status Drift</div>
              <h2 className="font-display text-2xl font-black tracking-tighter mt-1">
                {driftCount === 0
                  ? "No drift detected. All orders are clean."
                  : `${driftCount} drift order${driftCount === 1 ? "" : "s"} need attention.`}
              </h2>
              <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                A drift order has a paid-lifecycle <code className="mono">status</code> (paid, printing, ready, completed) but its <code className="mono">payment_status</code> was left behind (usually because an M-Pesa STK callback was dropped and staff advanced the order manually). Downloads still work thanks to iter19 tolerance, but revenue/stats aggregations that filter strictly on <code className="mono">payment_status == &quot;paid&quot;</code> will under-count until these rows are healed.
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={load}
              data-testid="data-health-refresh"
              disabled={repairing}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Rescan
            </button>
            {isSuper && driftCount > 0 && (
              <button
                onClick={repairAll}
                data-testid="data-health-repair-all"
                disabled={repairing}
                className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border-2 border-[#059669] bg-[#059669] text-white hover:bg-[#047857] hover:border-[#047857] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {repairing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
                Repair all ({driftCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table of drift rows */}
      {rows.length === 0 ? null : (
        <div className="border border-[#E5E5E5] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Tracking</th>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Customer</th>
                <th className="p-3 text-right text-[10px] uppercase tracking-[0.15em] font-bold">Total</th>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Status</th>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Payment</th>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Paid via</th>
                <th className="p-3 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Updated</th>
                {isSuper && <th className="p-3 text-right text-[10px] uppercase tracking-[0.15em] font-bold">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-[#E5E5E5] hover:bg-[#F59E0B]/5" data-testid={`drift-row-${o.tracking_number}`}>
                  <td className="p-3 mono font-bold">
                    <a href={`/track?tn=${o.tracking_number}`} target="_blank" rel="noreferrer" className="text-[#0A0A0A] hover:text-[#059669] inline-flex items-center gap-1">
                      {o.tracking_number}
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  </td>
                  <td className="p-3">{o.customer_name || "—"}</td>
                  <td className="p-3 text-right mono">{ksh(o.total)}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-1 border border-[#059669] text-[#059669] text-[10px] uppercase tracking-[0.15em] font-bold">
                      {o.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-1 border border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E] text-[10px] uppercase tracking-[0.15em] font-bold">
                      {o.payment_status || "unset"}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-[#525252]">{o.paid_via || "—"}</td>
                  <td className="p-3 text-xs text-[#525252]" title={o.updated_at}>{timeAgo(o.updated_at)}</td>
                  {isSuper && (
                    <td className="p-3 text-right">
                      <button
                        onClick={() => repairOne(o.id)}
                        data-testid={`drift-repair-${o.tracking_number}`}
                        disabled={repairing}
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] bg-white hover:bg-[#059669] hover:border-[#059669] hover:text-white transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <Wrench className="w-3 h-3" />
                        Heal
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Read-only viewer hint */}
      {!isSuper && driftCount > 0 && (
        <p className="text-xs text-[#525252]">
          You have read-only access to Data Health. Ask a super-admin to run the repair.
        </p>
      )}
    </div>
  );
}
